import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, Filter, Search, Calendar, Clock, CheckCircle, Trash2, Grid, List, Image as ImageIcon, HelpCircle, AlertCircle, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QCIncidents = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [sortBy, setSortBy] = useState('age'); // age, date_registered
  const [suppliers, setSuppliers] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // grid or table

  // Check if user is a supplier - handle different role formats
  const isSupplier = user?.role?.toLowerCase() === 'supplier' || 
                     user?.roles?.some(r => r.toLowerCase().includes('supplier'));

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log('Fetching incidents for user:', user);
      console.log('User ID:', user?.id);
      console.log('User Role:', user?.role);
      
      const response = await axios.get(`${API}/qc-incidents`, { headers });
      
      console.log('Incidents response:', response.data);
      console.log('Number of incidents:', response.data.incidents?.length);
      
      if (isSupplier && response.data.incidents?.length > 0) {
        console.log('First incident supplier_id:', response.data.incidents[0].supplier_id);
        console.log('Logged-in user ID:', user?.id);
        console.log('IDs match:', response.data.incidents[0].supplier_id === user?.id);
      }
      
      setIncidents(response.data.incidents || []);
      
      // Extract unique suppliers
      const uniqueSuppliers = [...new Set(
        response.data.incidents.map(inc => inc.supplier_name).filter(Boolean)
      )];
      setSuppliers(uniqueSuppliers);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (incidentDate) => {
    if (!incidentDate) return 0;
    const incident = new Date(incidentDate);
    const now = new Date();
    const diffTime = Math.abs(now - incident);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateHandlingTime = (dateRegistered, solvedDate) => {
    const registered = new Date(dateRegistered);
    const solved = new Date(solvedDate);
    const diffTime = Math.abs(solved - registered);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getAgeColor = (age) => {
    if (age <= 5) return 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800';
    if (age <= 14) return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
    return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800';
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'Critical':
        return 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'High':
        return 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'Medium':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'Low':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const handleDeleteIncident = async (incidentId) => {
    if (!window.confirm('Are you sure you want to delete this incident? This action cannot be undone.')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/qc-incidents/${incidentId}`, { headers });
      
      // Refresh the incidents list
      fetchIncidents();
    } catch (error) {
      console.error('Failed to delete incident:', error);
      alert('Failed to delete incident. Please try again.');
    }
  };

  const filteredAndSortedIncidents = () => {
    let filtered = incidents.filter(incident => {
      const matchesSearch = 
        incident.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.project_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSupplier = filterSupplier === 'all' || incident.supplier_name === filterSupplier;
      
      return matchesSearch && matchesSupplier;
    });

    // Separate solved and unsolved
    const unsolved = filtered.filter(inc => inc.status === 'Unsolved');
    const solved = filtered.filter(inc => inc.status === 'Solved');

    // Sort unsolved by age or date
    if (sortBy === 'age') {
      unsolved.sort((a, b) => calculateAge(b.incident_date) - calculateAge(a.incident_date));
    } else {
      unsolved.sort((a, b) => new Date(b.incident_date) - new Date(a.incident_date));
    }

    // Sort solved by handling time or date
    if (sortBy === 'age') {
      solved.sort((a, b) => {
        const handlingA = calculateHandlingTime(a.date_registered, a.solved_date);
        const handlingB = calculateHandlingTime(b.date_registered, b.solved_date);
        return handlingB - handlingA;
      });
    } else {
      solved.sort((a, b) => new Date(b.incident_date) - new Date(a.incident_date));
    }

    return { unsolved, solved };
  };

  const { unsolved, solved } = filteredAndSortedIncidents();

  // Helper function to calculate incident statistics
  const getIncidentStats = (incident) => {
    const failurePoints = incident.failure_points ? incident.failure_points.split('\n').filter(p => p.trim()) : [];
    const responses = incident.failure_points_responses || [];
    
    // Count solved action items (those with approved responses)
    let solvedCount = 0;
    failurePoints.forEach((_, index) => {
      const pointResponses = responses.filter(r => r.failure_point_index === index);
      const hasApprovedResponse = pointResponses.some(r => r.status === 'approved');
      if (hasApprovedResponse) {
        solvedCount++;
      }
    });
    
    // Unsolved = total - solved
    const unsolvedCount = failurePoints.length - solvedCount;
    
    // Count pending approval solutions (supplier submitted, needs employee action)
    const pendingApprovalCount = responses.filter(r => r.status === 'pending').length;
    
    return {
      solvedCount,
      totalActionItems: failurePoints.length,
      unsolvedCount,
      pendingApprovalCount,
      isFullySolved: failurePoints.length > 0 && solvedCount === failurePoints.length
    };
  };


  // Calculate overall statistics for all filtered incidents
  const calculateOverallStats = () => {
    const allFiltered = [...unsolved, ...solved];
    
    // Count unsolved cases
    const unsolvedCasesCount = unsolved.length;
    
    // Calculate average age (only for unsolved)
    const totalAge = unsolved.reduce((sum, incident) => sum + calculateAge(incident.incident_date), 0);
    const averageAge = unsolved.length > 0 ? Math.round(totalAge / unsolved.length) : 0;
    
    // Find oldest case (only among unsolved)
    const oldestAge = unsolved.length > 0 
      ? Math.max(...unsolved.map(incident => calculateAge(incident.incident_date))) 
      : 0;
    
    // Count total unanswered action items and pending solutions across all incidents
    let totalUnansweredActions = 0;
    let totalPendingSolutions = 0;
    allFiltered.forEach(incident => {
      const stats = getIncidentStats(incident);
      totalUnansweredActions += stats.unsolvedCount;
      totalPendingSolutions += stats.pendingApprovalCount;
    });
    
    return {
      unsolvedCasesCount,
      averageAge,
      oldestAge,
      totalUnansweredActions,
      totalPendingSolutions
    };
  };

  const overallStats = calculateOverallStats();

  // Get severity color classes based on value and thresholds
  const getSeverityColor = (value, greenMax, redMin) => {
    // Green range
    if (value <= greenMax) {
      return {
        bg: 'bg-green-50 dark:bg-green-900/20',
        border: 'border-green-200 dark:border-green-800',
        text: 'text-green-600 dark:text-green-400',
        numberText: 'text-green-700 dark:text-green-300',
        subText: 'text-green-500 dark:text-green-400'
      };
    }
    
    // Red range
    if (value >= redMin) {
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        text: 'text-red-600 dark:text-red-400',
        numberText: 'text-red-700 dark:text-red-300',
        subText: 'text-red-500 dark:text-red-400'
      };
    }
    
    // Calculate gradient position (0 = green, 1 = red)
    const position = (value - greenMax) / (redMin - greenMax);
    
    // Yellow-orange range (transition)
    if (position < 0.5) {
      return {
        bg: 'bg-yellow-50 dark:bg-yellow-900/20',
        border: 'border-yellow-200 dark:border-yellow-800',
        text: 'text-yellow-600 dark:text-yellow-400',
        numberText: 'text-yellow-700 dark:text-yellow-300',
        subText: 'text-yellow-500 dark:text-yellow-400'
      };
    } else {
      return {
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        border: 'border-orange-200 dark:border-orange-800',
        text: 'text-orange-600 dark:text-orange-400',
        numberText: 'text-orange-700 dark:text-orange-300',
        subText: 'text-orange-500 dark:text-orange-400'
      };
    }
  };

  // Get color classes for each stat
  const unsolvedCasesColor = getSeverityColor(overallStats.unsolvedCasesCount, 0, 5);
  const averageAgeColor = getSeverityColor(overallStats.averageAge, 5, 30);
  const oldestCaseColor = getSeverityColor(overallStats.oldestAge, 5, 30);
  const unsolvedActionsColor = getSeverityColor(overallStats.totalUnansweredActions, 5, 20);



  const renderIncidentCard = (incident) => {
    const age = calculateAge(incident.incident_date);
    const handlingTime = incident.solved_date ? calculateHandlingTime(incident.date_registered, incident.solved_date) : null;
    const isUnsolved = incident.status === 'Unsolved';
    const hasPhotos = incident.photos && incident.photos.length > 0;
    const thumbnail = hasPhotos ? incident.photos[0] : null;
    const stats = getIncidentStats(incident);

    return (
      <div
        key={incident.id}
        onClick={() => navigate(`/qc-incidents/${incident.id}`)}
        className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700 overflow-hidden"
      >
        {/* Thumbnail Image */}
        {thumbnail && (
          <div className="w-full h-32 bg-gray-100 dark:bg-gray-700 relative">
            <img
              src={thumbnail}
              alt="Incident"
              className="w-full h-full object-cover"
            />
            {hasPhotos && incident.photos.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {incident.photos.length}
              </div>
            )}
          </div>
        )}
        
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {incident.project_name || 'Unnamed Project'}
              </h3>
              {incident.project_id && (
                <p className="text-sm text-gray-500 dark:text-gray-400">ID: {incident.project_id}</p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getImportanceColor(incident.importance)}`}>
                {incident.importance}
              </span>
              
              {/* Action Buttons - Employee Only */}
              {!isSupplier && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteIncident(incident.id);
                  }}
                  className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                  title="Delete incident"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {incident.description}
          </p>

          {/* Status Indicators */}
          {stats.totalActionItems > 0 && (
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {/* Star - Solved/Total ratio */}
              <div className={`flex items-center gap-1 text-xs font-medium ${stats.isFullySolved ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`} 
                   title={`${stats.solvedCount} of ${stats.totalActionItems} action items solved`}>
                <Star className="w-4 h-4" fill="currentColor" />
                <span>({stats.solvedCount}/{stats.totalActionItems})</span>
              </div>
              
              {/* Red Question Mark - Unsolved action items */}
              {stats.unsolvedCount > 0 && (
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400 text-xs" title={`${stats.unsolvedCount} unsolved action item${stats.unsolvedCount > 1 ? 's' : ''}`}>
                  <HelpCircle className="w-4 h-4" />
                  <span className="font-medium">({stats.unsolvedCount})</span>
                </div>
              )}
              
              {/* Green Exclamation - Pending supplier solutions */}
              {stats.pendingApprovalCount > 0 && (
                <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs" title={`${stats.pendingApprovalCount} solution${stats.pendingApprovalCount > 1 ? 's' : ''} submitted by supplier, awaiting approval`}>
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">({stats.pendingApprovalCount})</span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{new Date(incident.incident_date).toLocaleDateString()}</span>
              </div>
              <div className="text-gray-500 dark:text-gray-400">
                Supplier: <span className="font-medium">{incident.supplier_name}</span>
              </div>
            </div>

            <div className={`flex items-center gap-1 px-3 py-1 rounded-full border font-medium ${getAgeColor(isUnsolved ? age : handlingTime)}`}>
              <Clock className="w-4 h-4" />
              {isUnsolved ? (
                <span>Age: {age} days</span>
              ) : (
                <span>Handled: {handlingTime} days</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderIncidentsTable = (incidents) => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Photo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Importance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Age/Handling</th>
                {!isSupplier && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {incidents.map(incident => {
                const age = calculateAge(incident.incident_date);
                const handlingTime = incident.solved_date ? calculateHandlingTime(incident.date_registered, incident.solved_date) : null;
                const isUnsolved = incident.status === 'Unsolved';
                const hasPhotos = incident.photos && incident.photos.length > 0;
                const thumbnail = hasPhotos ? incident.photos[0] : null;
                const stats = getIncidentStats(incident);

                return (
                  <tr 
                    key={incident.id}
                    onClick={() => navigate(`/qc-incidents/${incident.id}`)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      {thumbnail ? (
                        <div className="relative w-16 h-16">
                          <img src={thumbnail} alt="Incident" className="w-full h-full object-cover rounded" />
                          {hasPhotos && incident.photos.length > 1 && (
                            <div className="absolute bottom-0 right-0 bg-black bg-opacity-70 text-white text-xs px-1 rounded-tl">
                              {incident.photos.length}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{incident.project_name || 'Unnamed'}</div>
                      {incident.project_id && <div className="text-sm text-gray-500 dark:text-gray-400">{incident.project_id}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{incident.supplier_name}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{new Date(incident.incident_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getImportanceColor(incident.importance)}`}>
                        {incident.importance}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {stats.totalActionItems > 0 ? (
                          <>
                            {/* Star - Solved/Total ratio */}
                            <div className={`flex items-center gap-1 text-xs font-medium ${stats.isFullySolved ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`} 
                                 title={`${stats.solvedCount}/${stats.totalActionItems} solved`}>
                              <Star className="w-4 h-4" fill="currentColor" />
                              <span>({stats.solvedCount}/{stats.totalActionItems})</span>
                            </div>
                            
                            {/* Red Question Mark - Unsolved */}
                            {stats.unsolvedCount > 0 && (
                              <div className="flex items-center gap-1 text-red-600 dark:text-red-400" title={`${stats.unsolvedCount} unsolved`}>
                                <HelpCircle className="w-4 h-4" />
                                <span className="text-xs">({stats.unsolvedCount})</span>
                              </div>
                            )}
                            
                            {/* Green Exclamation - Pending approval */}
                            {stats.pendingApprovalCount > 0 && (
                              <div className="flex items-center gap-1 text-green-600 dark:text-green-400" title={`${stats.pendingApprovalCount} awaiting approval`}>
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-xs">({stats.pendingApprovalCount})</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400 text-xs">No actions</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getAgeColor(isUnsolved ? age : handlingTime)}`}>
                        {isUnsolved ? `${age} days` : `${handlingTime} days`}
                      </span>
                    </td>
                    {!isSupplier && (
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteIncident(incident.id);
                          }}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors inline-flex"
                          title="Delete incident"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading incidents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-orange-600 dark:text-orange-400" />
              QC Incidents
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {isSupplier ? 'View and respond to your quality control incidents' : 'Quality Control Incident Management'}
            </p>
          </div>

          {!isSupplier && (
            <button
              onClick={() => navigate('/qc-incidents/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Register Incident
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by project name, ID, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {!isSupplier && (
            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Suppliers</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
          )}

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="age">Sort by Age/Handling Time</option>
            <option value="date_registered">Sort by Incident Date</option>
          </select>

          {/* View Toggle */}
          <div className="flex gap-2 border border-gray-300 dark:border-gray-600 rounded-lg p-1 bg-white dark:bg-gray-700">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
              title="Grid view"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
              title="Table view"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>


        {/* Overall Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Unsolved Cases */}
          <div className={`${unsolvedCasesColor.bg} border ${unsolvedCasesColor.border} rounded-lg p-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${unsolvedCasesColor.text} font-medium mb-1`}>Unsolved Cases</p>
                <p className={`text-3xl font-bold ${unsolvedCasesColor.numberText}`}>{overallStats.unsolvedCasesCount}</p>
              </div>
              <AlertTriangle className={`w-10 h-10 ${unsolvedCasesColor.text} opacity-50`} />
            </div>
          </div>

          {/* Average Age */}
          <div className={`${averageAgeColor.bg} border ${averageAgeColor.border} rounded-lg p-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${averageAgeColor.text} font-medium mb-1`}>Average Age</p>
                <p className={`text-3xl font-bold ${averageAgeColor.numberText}`}>{overallStats.averageAge}</p>
                <p className={`text-xs ${averageAgeColor.subText}`}>days</p>
              </div>
              <Clock className={`w-10 h-10 ${averageAgeColor.text} opacity-50`} />
            </div>
          </div>

          {/* Oldest Case */}
          <div className={`${oldestCaseColor.bg} border ${oldestCaseColor.border} rounded-lg p-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${oldestCaseColor.text} font-medium mb-1`}>Oldest Case</p>
                <p className={`text-3xl font-bold ${oldestCaseColor.numberText}`}>{overallStats.oldestAge}</p>
                <p className={`text-xs ${oldestCaseColor.subText}`}>days old</p>
              </div>
              <Calendar className={`w-10 h-10 ${oldestCaseColor.text} opacity-50`} />
            </div>
          </div>

          {/* Unsolved Actions */}
          <div className={`${unsolvedActionsColor.bg} border ${unsolvedActionsColor.border} rounded-lg p-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${unsolvedActionsColor.text} font-medium mb-1`}>Unsolved Actions</p>
                <p className={`text-3xl font-bold ${unsolvedActionsColor.numberText}`}>{overallStats.totalUnansweredActions}</p>
                <p className={`text-xs ${unsolvedActionsColor.subText}`}>action items</p>
              </div>
              <HelpCircle className={`w-10 h-10 ${unsolvedActionsColor.text} opacity-50`} />
            </div>
          </div>

          {/* Solutions to Review */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">Solutions to Review</p>
                <p className="text-3xl font-bold text-green-700 dark:text-green-300">{overallStats.totalPendingSolutions}</p>
                <p className="text-xs text-green-500 dark:text-green-400">pending approval</p>
              </div>
              <AlertCircle className="w-10 h-10 text-green-600 dark:text-green-400 opacity-50" />
            </div>
          </div>
        </div>

      </div>

      {/* Unsolved Incidents */}
      {unsolved.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            Unsolved ({unsolved.length})
          </h2>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {unsolved.map(renderIncidentCard)}
            </div>
          ) : (
            renderIncidentsTable(unsolved)
          )}
        </div>
      )}

      {/* Separator */}
      {unsolved.length > 0 && solved.length > 0 && (
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t-2 border-gray-300 dark:border-gray-600"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-gray-50 dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
              Solved Incidents
            </span>
          </div>
        </div>
      )}

      {/* Solved Incidents */}
      {solved.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-500" />
            Solved ({solved.length})
          </h2>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {solved.map(renderIncidentCard)}
            </div>
          ) : (
            renderIncidentsTable(solved)
          )}
        </div>
      )}

      {/* Empty State */}
      {unsolved.length === 0 && solved.length === 0 && (
        <div className="text-center py-12">
          <AlertTriangle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No incidents found</p>
          {!isSupplier && (
            <button
              onClick={() => navigate('/qc-incidents/new')}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Register First Incident
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default QCIncidents;
